import { booker } from "@/content";
import { Rich } from "@/lib/rich";

export default function Hero() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Gradient blobs background */}
      <div className="absolute inset-0 bg-gray-950">
        <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-yellow-400/40 blur-[120px]" />
        <div className="absolute top-[10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-red-500/35 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[20%] w-[550px] h-[550px] rounded-full bg-violet-600/35 blur-[130px]" />
        <div className="absolute bottom-[5%] right-[10%] w-[400px] h-[400px] rounded-full bg-cyan-400/30 blur-[110px]" />
        <div className="absolute top-[40%] left-[40%] w-[350px] h-[350px] rounded-full bg-green-400/25 blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-20 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left content */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/80 text-xs font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-green-400 to-cyan-400 animate-pulse inline-block"></span>{booker.hero.badge}</div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
              <Rich text={booker.hero.title} accentClassName="bg-gradient-to-r from-green-300 via-cyan-300 to-violet-400 bg-clip-text text-transparent" />
            </h1>

            <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-4 max-w-xl mx-auto lg:mx-0">{booker.hero.lead}</p>
            <p className="text-white/50 text-base leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0">{booker.hero.text}</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="#contact"
                className="px-8 py-4 rounded-2xl bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 text-white font-bold text-base shadow-lg hover:scale-105 transition-transform cursor-pointer whitespace-nowrap"
              >{booker.hero.primaryCta.label}</a>
              <a
                href="#how-it-works"
                className="px-8 py-4 rounded-2xl border border-white/30 text-white font-semibold text-base hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >{booker.hero.secondaryCta.label}</a>
            </div>

            {/* Stats */}
            <div className="mt-14 flex flex-wrap gap-8 justify-center lg:justify-start">
              {booker.hero.stats.map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className={`text-2xl font-extrabold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>{stat.value}</div>
                  <div className="text-white/50 text-xs mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: UI mockup */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[520px]">
              {/* Glow behind card */}
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/40 via-cyan-400/30 to-green-400/30 blur-[60px] rounded-3xl" />
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-5 shadow-2xl">
                {/* Mock header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-br from-yellow-400 to-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-gradient-to-br from-green-400 to-cyan-400"></div>
                    <div className="w-3 h-3 rounded-full bg-gradient-to-br from-violet-500 to-pink-400"></div>
                  </div>
                  <span className="text-white/60 text-xs font-medium">{booker.hero.mockup.caption}</span>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-calendar-line text-white/60 text-sm"></i>
                  </div>
                </div>

                {/* Calendar grid */}
                <div className="bg-white/5 rounded-2xl p-4 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-semibold text-sm">{booker.hero.mockup.month}</span>
                    <div className="flex gap-2">
                      <button className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10 cursor-pointer">
                        <i className="ri-arrow-left-s-line text-white/70 text-xs"></i>
                      </button>
                      <button className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10 cursor-pointer">
                        <i className="ri-arrow-right-s-line text-white/70 text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center">
                    {booker.hero.mockup.weekdays.map(d => (
                      <div key={d} className="text-white/40 dark:text-white/55 text-[10px] font-medium py-1">{d}</div>
                    ))}
                    {[...Array(4)].map((_, i) => (
                      <div key={`e${i}`} className="py-1.5"></div>
                    ))}
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
                      <div
                        key={day}
                        className={`py-1.5 text-xs rounded-lg cursor-pointer transition-all ${
                          day === 28
                            ? "bg-gradient-to-br from-yellow-400 to-red-400 text-white font-bold"
                            : day === 15 || day === 22
                            ? "bg-gradient-to-br from-green-400/60 to-cyan-400/60 text-white font-semibold"
                            : day === 10 || day === 17 || day === 24
                            ? "bg-violet-500/40 text-white/80"
                            : "text-white/60 hover:bg-white/10"
                        }`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Booking slots */}
                <div className="space-y-2">
                  {booker.hero.mockup.slots.map((slot) => (
                    <div
                      key={slot.time}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gradient-to-r ${slot.gradient} backdrop-blur-sm`}
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/20">
                        <i className={`${slot.icon} text-white text-sm`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="text-white text-xs font-semibold">{slot.title}</div>
                        <div className="text-white/70 text-[10px]">{slot.time}</div>
                      </div>
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 cursor-pointer">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white dark:from-[#0C0C0E] to-transparent z-10" />
    </section>
  );
}